import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Receipt, KeyRound } from "lucide-react";
import { PageShell } from "@/components/site/page-shell";
import { getOrderSummary } from "@/lib/checkout.functions";
import { OrderTracking } from "@/components/orders/order-tracking";

export const Route = createFileRoute("/orders/$orderId")({
  component: OrderConfirmationPage,
  head: () => ({
    meta: [
      { title: "Order Confirmation — CoderLity" },
      { name: "robots", content: "noindex" },
    ],
  }),
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="text-3xl font-bold">Order not found</h1>
        <Link to="/" className="mt-6 inline-block text-primary">Back home</Link>
      </div>
    </PageShell>
  ),
});

function OrderConfirmationPage() {
  const { orderId } = Route.useParams();
  const fetchOrder = useServerFn(getOrderSummary);
  const { data: order, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => fetchOrder({ data: { order_id: orderId } }),
  });

  if (isLoading) {
    return (
      <PageShell>
        <div className="mx-auto max-w-3xl px-4 py-32 text-center text-muted-foreground">Loading order…</div>
      </PageShell>
    );
  }
  if (!order) throw notFound();

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 lg:px-8 py-16">
        <div className="text-center">
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="mt-5 text-3xl font-bold tracking-tight">Thank you for your order</h1>
          <p className="mt-2 text-muted-foreground">
            Order #{order.id.slice(0, 8).toUpperCase()} · status{" "}
            <span className="font-medium capitalize text-foreground">{order.status}</span>
          </p>
          {order.guest_email && (
            <p className="mt-1 text-sm text-muted-foreground">
              We've sent details to {order.guest_email}
            </p>
          )}
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
            <Receipt className="h-4 w-4 text-primary" /> Items
          </div>
          <ul className="divide-y divide-border">
            {order.items.map((i) => (
              <li key={i.id} className="py-3 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{i.product_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {i.variant_name} · Qty {i.qty}
                      {i.fulfillment_status && (
                        <span className="ml-2 capitalize text-foreground/70">· {i.fulfillment_status}</span>
                      )}
                    </div>
                  </div>
                  <div className="font-semibold">
                    ${((i.unit_price_cents * i.qty) / 100).toFixed(2)}
                  </div>
                </div>
                {i.license_keys && i.license_keys.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {i.license_keys.map((k) => (
                      <div
                        key={k.id}
                        className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5"
                      >
                        <KeyRound className="h-3.5 w-3.5 text-primary shrink-0" />
                        <code className="flex-1 min-w-0 break-all font-mono text-xs">{k.key_value}</code>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span>${(order.subtotal_cents / 100).toFixed(2)}</span>
            </div>
            {order.discount_cents > 0 && (
              <div className="flex justify-between text-emerald-500">
                <span>Discount</span>
                <span>−${(order.discount_cents / 100).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 text-base font-semibold">
              <span>Total</span>
              <span className="text-gradient text-xl">${(order.total_cents / 100).toFixed(2)} {order.currency}</span>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <OrderTracking orderId={order.id} />
        </div>

        <div className="mt-8 rounded-xl border border-dashed border-border bg-card/50 p-5 text-sm text-muted-foreground">
          Payment processing isn't enabled yet — your order is recorded as <strong>pending</strong>.
          Once payments are activated, you'll be redirected to complete the charge.
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-white shadow-glow">
            Back home
          </Link>
        </div>
      </div>
    </PageShell>
  );
}
